import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { tokens } from '../../theme';

export interface ConfirmOptions {
  /** Pytanie, np. „Anulować wizytę?” */
  title: React.ReactNode;
  /** Czego dotyczy operacja — nazwa, klient, termin. Wyswietlane w wyroznionym polu. */
  details?: React.ReactNode;
  /** Skutek operacji jednym zdaniem, np. „Termin zwolni się w kalendarzu.” */
  description?: React.ReactNode;
  /** Przycisk potwierdzenia nazywa skutek: „Anuluj wizytę”, „Usuń markę”. */
  confirmLabel: string;
  /** Przycisk rezygnacji, domyslnie „Wróć”. */
  cancelLabel?: string;
  /** `danger` dla operacji nieodwracalnych lub niszczacych. */
  tone?: 'danger' | 'primary';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Zastepuje window.confirm oknem w stylu aplikacji. Montowany raz w App. */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  const danger = (options?.tone ?? 'danger') === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!options} onClose={() => close(false)} maxWidth="xs" fullWidth aria-labelledby="confirm-dialog-title">
        {options && (
          <>
            <DialogTitle id="confirm-dialog-title">{options.title}</DialogTitle>
            <DialogContent>
              {options.details && (
                <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: options.description ? 1.5 : 0, fontSize: '0.90625rem', color: tokens.ink }}>
                  {options.details}
                </Box>
              )}
              {options.description && (
                <Typography component="div" sx={{ color: tokens.inkSoft, fontSize: '0.90625rem' }}>
                  {options.description}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant="text" onClick={() => close(false)} autoFocus={danger}>
                {options.cancelLabel || 'Wróć'}
              </Button>
              <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={() => close(true)} autoFocus={!danger}>
                {options.confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
};

/**
 * const confirm = useConfirm();
 * if (!(await confirm({ title: 'Usunąć markę?', confirmLabel: 'Usuń markę' }))) return;
 */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Awaryjnie (np. w testach bez providera) — zachowanie jak dawniej.
    return async (opts) => window.confirm(typeof opts.title === 'string' ? opts.title : opts.confirmLabel);
  }
  return ctx;
};

export default ConfirmProvider;
